import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { getDealRoomBrandedName } from '../../../utils/locale';
import { gql, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import SearchSidebar from './SearchSidebar';
import MapView from './MapView';
import { regionOptionsForCountry, regionLabelForCountry } from '../../../lib/regionOptions';
import ListCard from './ListCard';
import { openSuccessionDrawer } from '../../../actions/successionDrawerActionCreators';
import SuccessionDrawerWithListener from '../../../containers/Succession/SuccessionDrawerWithListener';
import SuccessionThemeProvider from '../SuccessionThemeProvider';
import ProfileIncompleteEmptyState from './ProfileIncompleteEmptyState';
import Button from '../../../../../ui/canon/Button';
import Card from '../../../../../ui/canon/Card';
import Badge from '../../../../../ui/canon/Badge';
import { PageContainer } from '../../../../../ui/layouts/PageContainer';

import { S3Image, getAvatarStyle } from '../../../lib/s3';
import { currencySymbol } from '../../../utils/formatters';
import {
  SparklesIcon,
  FunnelIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  MapPinIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import queryString from 'query-string';
import { omitBy } from 'lodash';
import { useHistory } from 'react-router-dom';

// Marketplace Approval Ineligible Empty State — user must be approved before using search
const ApprovalIneligibleEmptyState = ({ approvalState, className = '' }) => {
  const isDenied = approvalState === 'denied' || approvalState === 2;
  const title = isDenied ? 'Search Access Not Granted' : 'Pending Home Office Approval';
  const description = isDenied
    ? 'Your marketplace access request was not approved, so search is unavailable. Please update your profile and contact support if you believe this is an error.'
    : "You're almost there. Your profile is being reviewed by the home office. Once approved, you'll be able to use search to find partners on the marketplace.";
  const IconComponent = isDenied ? ShieldCheckIcon : LockClosedIcon;
  const accent = isDenied ? 'bg-succession-error/10 text-succession-error' : 'bg-succession-warning/10 text-succession-warning';

  return (
    <div className={`flex flex-col items-center py-6 px-4 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-succession-bg-secondary rounded-2xl border border-succession-border p-8 md:p-10 max-w-2xl text-center"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${accent} mb-6`}
        >
          <IconComponent className="w-10 h-10" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-2xl md:text-3xl font-bold mb-4 text-succession-text-primary"
        >
          {title}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-succession-text-secondary text-lg mb-6 leading-relaxed"
        >
          {description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <a
            href="/succession/launchpad"
            className="inline-flex items-center gap-2 bg-succession-primary hover:bg-succession-primary/90 text-white px-8 py-3 rounded-lg font-medium transition-colors"
          >
            Return to LaunchPad
            <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </motion.div>
      </motion.div>
    </div>
  );
};

// Create a fallback history object when not using React Router
const createFallbackHistory = () => {
  return {
    location: { search: window.location.search, pathname: window.location.pathname },
    push: ({ pathname, search }) => {
      const newUrl = pathname + (search ? search : '');
      window.history.pushState({}, '', newUrl);
    }
  };
};

// Query for list view (includes detailed user information)
const LIST_VIEW_QUERY = gql`
  query UserListSearch(
    $prompt: String,
    $userName: String,
    $page: Int, 
    $perPage: Int,
    $locationWithin: String,
    $transitionGoalsName: [String!],
    $planningPreference: String,
    $province: String,
    $isStudent: Boolean,
    $ampTraineeStatus: Boolean,
    $hasPartialSales: Boolean,
    $partialSaleLocations: [String!],
    $discretionaryInvestmentModels: Boolean,
    $annualRevenueGt: Float,
    $annualRevenueLt: Float,
    $grossProductionGt: Float,
    $grossProductionLt: Float,
    $assetsUnderManagementGt: Float,
    $assetsUnderManagementLt: Float,
    $yearsInPracticeGt: Int,
    $numberOfClientsGt: Int,
    $designations: [ID!], 
    $affiliations: [ID!], 
    $languages: [String!], 
    $primaryMarkets: [String!],
    $tags: [String!]
  ) {
    getCurrentUser {
      id
      motivation
      searchable_transition_goals
      profile_percentage_complete
      approval_state
    }
    getTransitionGoals
    company {
      id
      name
      branded_name
      agent_word
      deal_room_branded_name
      deal_room_branded_name_fr
      country
      available_primary_markets
      available_strengths
      features
      business_type
      google_maps_api_key
      avatar_background_color
      avatar_directory
      primary_color
      languages {
        id
        name
      }
    }
    availableDesignations {
      id
      name
    }
    availableAffiliations {
      id
      name
    }
    userSearch(
      prompt: $prompt
      userName: $userName
      page: $page
      perPage: $perPage
      locationWithin: $locationWithin
      transitionGoalsName: $transitionGoalsName
      planningPreference: $planningPreference
      province: $province
      isStudent: $isStudent
      ampTraineeStatus: $ampTraineeStatus
      hasPartialSales: $hasPartialSales
      partialSaleLocations: $partialSaleLocations
      discretionaryInvestmentModels: $discretionaryInvestmentModels
      annualRevenueGt: $annualRevenueGt
      annualRevenueLt: $annualRevenueLt
      grossProductionGt: $grossProductionGt
      grossProductionLt: $grossProductionLt
      assetsUnderManagementGt: $assetsUnderManagementGt
      assetsUnderManagementLt: $assetsUnderManagementLt
      yearsInPracticeGt: $yearsInPracticeGt
      numberOfClientsGt: $numberOfClientsGt
      designations: $designations
      affiliations: $affiliations
      languages: $languages
      primaryMarkets: $primaryMarkets
      tags: $tags
    ) {
      total_count
      items {
        id
        user_name
        fb_user_name
        allowed_name
        annual_revenue
        bio
        city
        province
        headline
        profile_picture
        assets_under_management
        gross_production
        annual_revenue
        number_of_clients
        years_practicing
        visible_profile_features_for_user
        conversation_with_me {
          id
        }
        note_count
        has_partial_sales
        is_student
        amp_trainee_status
        motivation
        recognition_level
      }
      aiResponse
    }
  }
`;

// Optimized query for map pins (only includes essential location data)
const MAP_PINS_QUERY = gql`
  query UserMapSearch(
    $prompt: String,
    $userName: String,
    $page: Int, 
    $perPage: Int,
    $locationWithin: String,
    $transitionGoalsName: [String!],
    $planningPreference: String,
    $province: String,
    $isStudent: Boolean,
    $ampTraineeStatus: Boolean,
    $hasPartialSales: Boolean,
    $partialSaleLocations: [String!],
    $discretionaryInvestmentModels: Boolean,
    $annualRevenueGt: Float,
    $annualRevenueLt: Float,
    $grossProductionGt: Float,
    $grossProductionLt: Float,
    $assetsUnderManagementGt: Float,
    $assetsUnderManagementLt: Float,
    $yearsInPracticeGt: Int,
    $numberOfClientsGt: Int,
    $designations: [ID!], 
    $affiliations: [ID!], 
    $languages: [String!], 
    $primaryMarkets: [String!],
    $tags: [String!]
  ) {
    getCurrentUser {
      id
      profile_percentage_complete
      approval_state
      company {
        id
        country
        available_strengths
        features
        avatar_background_color
        avatar_directory
        primary_color
      }
    }
    userSearch(
      prompt: $prompt
      userName: $userName
      page: $page
      perPage: $perPage
      locationWithin: $locationWithin
      transitionGoalsName: $transitionGoalsName
      planningPreference: $planningPreference
      province: $province
      isStudent: $isStudent
      ampTraineeStatus: $ampTraineeStatus
      hasPartialSales: $hasPartialSales
      partialSaleLocations: $partialSaleLocations
      discretionaryInvestmentModels: $discretionaryInvestmentModels
      annualRevenueGt: $annualRevenueGt
      annualRevenueLt: $annualRevenueLt
      grossProductionGt: $grossProductionGt
      grossProductionLt: $grossProductionLt
      assetsUnderManagementGt: $assetsUnderManagementGt
      assetsUnderManagementLt: $assetsUnderManagementLt
      yearsInPracticeGt: $yearsInPracticeGt
      numberOfClientsGt: $numberOfClientsGt
      designations: $designations
      affiliations: $affiliations
      languages: $languages
      primaryMarkets: $primaryMarkets
      tags: $tags
    ) {
      total_count
      items {
        id
        user_name
        fb_user_name
        allowed_name
        headline
        city
        province
        map_latitude
        map_longitude
      }
    }
  }
`;

// Inner component that uses the theme context
const SuccessionListSearchContent = ({ initialViewMode = 'list', history: propHistory, ...props }) => {
  // Create fallback history object if not provided via props
  const history = propHistory || createFallbackHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mapTypeId, setMapTypeId] = useState('roadmap');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [hoveredUserId, setHoveredUserId] = useState(null);
  const [searchParams, setSearchParams] = useState({
    prompt: '',
    page: 1,
    perPage: 20,
    aiMode: true  // Default to AI mode
  });
  const searchParamsRef = useRef(searchParams);
  const hasInitializedRef = useRef(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState(null);

  // Clean parameters before sending to GraphQL
  const cleanParams = useCallback((params) => {
    // Make a copy of params to avoid modifying the original
    const cleaned = { ...params };
    
    // Convert locationWithin to string explicitly to avoid GraphQL type errors
    if (cleaned.locationWithin !== undefined && cleaned.locationWithin !== null) {
      cleaned.locationWithin = String(cleaned.locationWithin);
    }
    
    // Remove null, undefined, and empty string values
    const result = omitBy(cleaned, value => {
      if (value === null || value === undefined || value === '') return true;
      
      // Remove empty arrays
      if (Array.isArray(value) && value.length === 0) return true;
      
      return false;
    });
    
    // Special case: remove aiMode if it's false (default)
    if (!result.aiMode) {
      delete result.aiMode;
    }
    
    return result;
  }, []);

  // Debounced list params to prevent rapid successive requests
  const listParams = useMemo(() => cleanParams(searchParams), [cleanParams, searchParams]);
  const [debouncedListParams, setDebouncedListParams] = useState(listParams);
  const listDebounceRef = useRef(null);

  useEffect(() => {
    // Clear any pending debounce
    if (listDebounceRef.current) {
      clearTimeout(listDebounceRef.current);
    }

    // Debounce the list params update
    listDebounceRef.current = setTimeout(() => {
      setDebouncedListParams(listParams);
    }, 500);

    return () => {
      if (listDebounceRef.current) {
        clearTimeout(listDebounceRef.current);
      }
    };
  }, [listParams]);

  // Execute the list query and handle loading state
  const { loading: listLoading, error: listError, data: listData } = useQuery(LIST_VIEW_QUERY, {
    variables: debouncedListParams,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      // Store the Google Maps API key once we receive it
      if (data?.company?.google_maps_api_key && !googleMapsApiKey) {
        setGoogleMapsApiKey(data.company.google_maps_api_key);
      }
      
      // If we got an AI response and we're in AI mode, add it to the conversation history
      if (searchParams.aiMode && 
          data?.userSearch?.aiResponse && 
          searchParams.prompt) {
        
        // Add the user's query to history if it doesn't exist already
        const lastMessage = conversationHistory[conversationHistory.length - 1];
        if (!lastMessage || lastMessage.role !== 'user' || lastMessage.content !== searchParams.prompt) {
          setConversationHistory(prev => [
            ...prev,
            {
              role: 'user',
              content: searchParams.prompt,
              timestamp: new Date().toISOString()
            }
          ]);
        }
        
        // Add AI's response to history
        setConversationHistory(prev => [
          ...prev,
          {
            role: 'assistant',
            content: data.userSearch.aiResponse,
            timestamp: new Date().toISOString()
          }
        ]);
      }
    }
  });

  const mapPinsParams = useMemo(() => (
    cleanParams({
      ...searchParams,
      page: 1,
      perPage: 1000,
    })
  ), [cleanParams, searchParams]);

  // Debounced map pins params to prevent rapid successive requests
  const [debouncedMapPinsParams, setDebouncedMapPinsParams] = useState(mapPinsParams);
  const mapPinsDebounceRef = useRef(null);

  useEffect(() => {
    // Clear any pending debounce
    if (mapPinsDebounceRef.current) {
      clearTimeout(mapPinsDebounceRef.current);
    }

    // Only debounce when in map view
    if (viewMode !== 'map') {
      setDebouncedMapPinsParams(mapPinsParams);
      return;
    }

    // Debounce the map pins params update
    mapPinsDebounceRef.current = setTimeout(() => {
      setDebouncedMapPinsParams(mapPinsParams);
    }, 500);

    return () => {
      if (mapPinsDebounceRef.current) {
        clearTimeout(mapPinsDebounceRef.current);
      }
    };
  }, [mapPinsParams, viewMode]);

  const { loading: pinsLoading, error: pinsError, data: pinsData } = useQuery(MAP_PINS_QUERY, {
    variables: debouncedMapPinsParams,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: viewMode !== 'map',
  });

  // Perform initial search when component mounts
  // Note: Empty deps is intentional - we only want to run this once on mount
  // using the initial searchParams state values
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    // Run an empty search on load with default parameters
    const initialSearch = { 
      page: 1,
      perPage: 20,
      aiMode: true,
      locationWithin: "5000" // Default to searching everywhere
    };
    
    // Update URL with params to trigger the search
    const urlParams = queryString.stringify(cleanParams(initialSearch));
    
    history.push({
      pathname: history.location.pathname,
      search: urlParams ? `?${urlParams}` : ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Parse location.search and update search parameters when URL changes
  useEffect(() => {
    try {
      if (!history || !history.location || !history.location.search) return;
      
      const parsedQuery = queryString.parse(history.location.search);
      const updatedParams = { ...searchParams };
      
      // Handle numeric parameters
      ['page', 'perPage'].forEach(key => {
        if (parsedQuery[key]) {
          updatedParams[key] = parseInt(parsedQuery[key], 10);
        }
      });
      
      // Handle locationWithin as string to prevent type errors
      if (parsedQuery.locationWithin) {
        updatedParams.locationWithin = String(parsedQuery.locationWithin);
      }
      
      // Handle boolean parameters
      ['aiMode', 'isStudent', 'ampTraineeStatus', 'hasPartialSales', 'discretionaryInvestmentModels'].forEach(key => {
        if (parsedQuery[key] !== undefined) {
          updatedParams[key] = parsedQuery[key] === 'true';
        }
      });
      
      // Handle array parameters
      ['transitionGoalsName', 'designations', 'affiliations', 'languages', 'primaryMarkets', 'tags'].forEach(key => {
        if (parsedQuery[key]) {
          const arrayValue = Array.isArray(parsedQuery[key]) ? parsedQuery[key] : [parsedQuery[key]];
          updatedParams[key] = arrayValue;
        }
      });
      
      // Handle other string parameters
      ['prompt', 'userName', 'province', 'planningPreference'].forEach(key => {
        if (parsedQuery[key]) {
          updatedParams[key] = parsedQuery[key];
        }
      });
      
      // Handle numeric range parameters
      [
        'annualRevenueGt', 'annualRevenueLt', 
        'grossProductionGt', 'grossProductionLt', 
        'assetsUnderManagementGt', 'assetsUnderManagementLt',
        'yearsInPracticeGt', 'numberOfClientsGt'
      ].forEach(key => {
        if (parsedQuery[key]) {
          updatedParams[key] = parseFloat(parsedQuery[key]);
        }
      });
      
      setSearchParams(updatedParams);
      
      // If prompt is present in URL, update search input value too
      if (parsedQuery.prompt) {
        setSearchInputValue(parsedQuery.prompt);
      }
    } catch (error) {
      console.error('Error parsing URL parameters:', error);
    }
  }, [history?.location?.search]);

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  // Persist Google Maps API key from listData
  useEffect(() => {
    if (listData?.company?.google_maps_api_key && !googleMapsApiKey) {
      setGoogleMapsApiKey(listData.company.google_maps_api_key);
    }
  }, [listData, googleMapsApiKey]);

  // Check if AI search is a supported feature for this company
  const hasAISearchFeature = listData?.company?.features?.includes('ai_search');

  // When the feature flag is resolved and ai_search is disabled, force aiMode off
  useEffect(() => {
    if (hasAISearchFeature === false && searchParams.aiMode) {
      handleAIModeChange(false);
    }
  }, [hasAISearchFeature]);

  // Handle view mode change
  const handleViewModeChange = (newMode) => {
    if (viewMode === newMode) return;

    // When switching to map view, default to manual search
    if (newMode === 'map' && searchParams.aiMode) {
      handleAIModeChange(false);
    }

    setViewMode(newMode);
  };

  // Watch for drawer user name in document.x
  useEffect(() => {
    const checkForUserName = () => {
      if (document.x && document.x.a) {
        dispatch(openSuccessionDrawer(document.x.a));
        // Reset for future use
        document.x.a = null;
      }
    };
    
    // Create global object if it doesn't exist
    if (!document.x) {
      document.x = {};
    }
    
    // Check periodically
    const interval = setInterval(checkForUserName, 500);
    return () => clearInterval(interval);
  }, [dispatch]);

  // When deep-linked with ?user_name= (e.g. admin "View Marketplace Profile"
  // from /admin-v2/users), seed document.x.a so the listener above opens that
  // advisor's profile drawer on load.
  useEffect(() => {
    if (props.selected_user_name) {
      if (!document.x) document.x = {};
      document.x.a = props.selected_user_name;
    }
  }, [props.selected_user_name]);

  // Handle search parameter changes
  const handleSearchChange = (field, value) => {
    setSearchParams(prevParams => {
      const newParams = {
        ...prevParams,
        [field]: value
      };
      
      // Reset to page 1 when any filter changes (except page and perPage changes)
      if (field !== 'page' && field !== 'perPage') {
        newParams.page = 1;
      }
      
      return newParams;
    });
    
    // Handle special case for search input field
    if (field === 'prompt') {
      setSearchInputValue(value);
    }
  };

  // When search is submitted, update URL with current search parameters
  const handleSearchSubmit = useCallback((incomingParams, isPagination = false) => {
    if (!history) return;
    
    // If no parameters provided, use current search parameters
    const updatedParams = incomingParams || { ...searchParams };
    
    // If using AI mode and we have search input value, use it as prompt
    if (updatedParams.aiMode && searchInputValue) {
      updatedParams.prompt = searchInputValue;
    }
    
    // Reset to page 1 on new search (not pagination) or when filters have changed
    if (!isPagination) {
      updatedParams.page = 1;
    }
    
    // Update searchParams state immediately to keep it in sync
    setSearchParams(updatedParams);
    
    // Clean and prepare URL parameters
    const urlParams = queryString.stringify(cleanParams(updatedParams));
    
    // Update URL (will trigger refetch via useEffect)
    history.push({
      pathname: history.location.pathname,
      search: urlParams ? `?${urlParams}` : ''
    });
  }, [history, searchParams, searchInputValue, cleanParams]);

  const manualFiltersState = useMemo(() => ({
    userName: searchParams.userName || '',
    province: searchParams.province || '',
    transitionGoalsName: searchParams.transitionGoalsName || [],
    assetsUnderManagementGt: searchParams.assetsUnderManagementGt || null,
    numberOfClientsGt: searchParams.numberOfClientsGt || null,
    yearsInPracticeGt: searchParams.yearsInPracticeGt || null,
  }), [
    searchParams.userName,
    searchParams.province,
    searchParams.transitionGoalsName,
    searchParams.assetsUnderManagementGt,
    searchParams.numberOfClientsGt,
    searchParams.yearsInPracticeGt,
  ]);

  const manualFiltersKey = useMemo(
    () => JSON.stringify(manualFiltersState),
    [manualFiltersState]
  );

  // Debounced effect for manual filter changes in map view
  // Note: handleSearchSubmit is intentionally excluded from deps to prevent infinite loops
  // We use searchParamsRef to get current state without adding it as a dependency
  useEffect(() => {
    if (viewMode !== 'map' || searchParams.aiMode) return;

    const timeout = setTimeout(() => {
      const nextParams = {
        ...searchParamsRef.current,
        ...manualFiltersState,
        page: 1,
      };
      handleSearchSubmit(nextParams);
    }, 400);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualFiltersKey, viewMode, searchParams.aiMode]);

  // Handle keyboard shortcuts
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSearchSubmit();
    }
  };

  // Toggle AI mode on/off
  const handleAIModeChange = (checked) => {
    // Update search parameters
    handleSearchChange('aiMode', checked);
    
    // Clear conversation history when turning AI mode off
    if (!checked) {
      setConversationHistory([]);
    } else {
      setFiltersOpen(false);
    }
  };

  // Reset conversation history and search to show all results
  const handleResetConversation = useCallback(() => {
    // Clear conversation history
    setConversationHistory([]);
    
    // Clear the search input
    setSearchInputValue('');
    
    // Reset search parameters to defaults while keeping AI mode
    const resetParams = {
      page: 1,
      perPage: searchParams.perPage || 20,
      aiMode: true,
      locationWithin: "5000" // Default to searching everywhere
    };
    
    // Update state and submit
    setSearchParams(resetParams);
    
    // Update URL to trigger the search
    const urlParams = queryString.stringify(cleanParams(resetParams));
    history.push({
      pathname: history.location.pathname,
      search: urlParams ? `?${urlParams}` : ''
    });
  }, [history, searchParams.perPage, cleanParams]);

  // Handle page change
  const handlePageChange = (newPage) => {
    const updatedParams = {
      ...searchParams,
      page: newPage
    };
    
    // Update searchParams state first
    setSearchParams(updatedParams);
    
    // Submit with pagination flag
    handleSearchSubmit(updatedParams, true); // Pass true to indicate this is pagination
    
    // Scroll to top when changing pages
    window.scrollTo(0, 0);
  };

  // Format conversation history for display
  const formatConversationHistory = (history) => {
    const result = [];
    let currentItem = null;
    
    for (const item of history) {
      if (item.role === 'user') {
        // Always add user messages as new items
        currentItem = {
          role: 'user',
          content: item.content,
          timestamp: item.timestamp
        };
        result.push(currentItem);
      } else if (item.role === 'assistant') {
        // Combine assistant messages if they come one after another
        if (currentItem && currentItem.role === 'assistant') {
          // Append to existing assistant message
          currentItem.content += '\n\n' + item.content;
        } else {
          // Create new assistant message
          currentItem = {
            role: 'assistant',
            content: item.content,
            timestamp: item.timestamp
          };
          result.push(currentItem);
        }
      }
    }
    
    return result;
  };

  // Render loading indicator
  const renderLoading = () => (
    <div className="flex justify-center items-center min-h-[200px] w-full">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-12 h-12 border-4 border-succession-border border-t-succession-primary rounded-full animate-spin transition-colors duration-300"></div>
        <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.list_search.loading_results' })}</span>
      </div>
    </div>
  );

  const listItems = listData?.userSearch?.items || [];
  const pinsItems = pinsData?.userSearch?.items || [];
  const totalCount = listData?.userSearch?.total_count || 0;

  const transitionGoalOptions = useMemo(() => {
    const goals = listData?.getCurrentUser?.searchable_transition_goals || [];
    return goals.map((goal) => {
      if (typeof goal === 'string') return goal;
      if (goal?.translated_name) return goal.translated_name;
      return goal?.name || goal?.id || '';
    }).filter(Boolean);
  }, [listData]);

  const formatMillionsLabel = (value) => {
    if (!value || value <= 0) return '0';
    return value >= 1000 ? `${(value / 1000).toFixed(1)}B` : `${value}`;
  };

  const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const formatCurrencyShort = (value) => {
    if (!value) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(numValue)) return 'N/A';
    const sym = currencySymbol();
    if (numValue >= 1000000) return `${sym}${(numValue / 1000000).toFixed(1)}M`;
    if (numValue >= 1000) return `${sym}${(numValue / 1000).toFixed(0)}K`;
    return `${sym}${Math.round(numValue)}`;
  };

  const minAumMillions = Math.max(
    0,
    Math.round((searchParams.assetsUnderManagementGt || 0) / 1000000)
  );
  const minClients = Math.max(0, Math.round(searchParams.numberOfClientsGt || 0));
  const minYears = Math.max(0, Math.round(searchParams.yearsInPracticeGt || 0));

  const activeFilterCount = [
    searchParams.userName,
    searchParams.province,
    (searchParams.transitionGoalsName || []).length > 0,
    searchParams.assetsUnderManagementGt,
    searchParams.numberOfClientsGt,
    searchParams.yearsInPracticeGt,
  ].filter(Boolean).length;

  const toggleTransitionGoal = (goal) => {
    const existing = searchParams.transitionGoalsName || [];
    const next = existing.includes(goal)
      ? existing.filter((item) => item !== goal)
      : [...existing, goal];
    handleSearchChange('transitionGoalsName', next);
  };

  const resetManualFilters = () => {
    setSearchParams(prevParams => ({
      ...prevParams,
      userName: '',
      province: '',
      transitionGoalsName: [],
      assetsUnderManagementGt: null,
      numberOfClientsGt: null,
      yearsInPracticeGt: null,
      page: 1,
    }));
  };

  const renderListCards = () => {
    if (listLoading && !listData) {
      return renderLoading();
    }

    if (listError) {
      return (
        <Card variant="outlined" size="lg" className="text-center bg-[var(--succession-error-bg)] text-[var(--succession-error)] border-[var(--succession-error)]">
          <p className="font-medium">{intl.formatMessage({ id: 'succession.search.list_search.error_occurred' })}</p>
          <p className="text-sm mt-2">{listError.message}</p>
        </Card>
      );
    }

    // Check if profile is complete enough to view search results
    const currentUser = listData?.getCurrentUser;
    const profilePercentage = currentUser?.profile_percentage_complete || 0;
    const isProfileComplete = profilePercentage >= 60;
    const companyFeatures = listData?.company?.features || [];
    const marketplaceApprovalEnabled = companyFeatures.includes('successor_approval_process') ||
                                       companyFeatures.includes('seller_approval_process') ||
                                       companyFeatures.includes('simple_marketplace_approval');
    const approvalState = currentUser?.approval_state;
    // no_motivation ("Here for Research") users have deliberately opted out of the
    // marketplace, so they were never submitted for home-office approval. Don't
    // park them behind the approval wall — let them browse. Visibility of
    // succession users is enforced server-side in the userSearch resolver.
    const optedOutOfMarketplace = currentUser?.motivation === 'no_motivation';
    const isApprovalIneligible = marketplaceApprovalEnabled && !optedOutOfMarketplace && (
      approvalState === 'pending' || approvalState === 0 ||
      approvalState === 'denied' || approvalState === 2
    );

    if (isApprovalIneligible) {
      return <ApprovalIneligibleEmptyState approvalState={approvalState} />;
    }

    if (!isProfileComplete) {
      return <ProfileIncompleteEmptyState profilePercentage={profilePercentage} isSeller={currentUser?.motivation === 'succession' && !!listData?.company?.features?.includes('rj_seller_visibility')} />;
    }

    if (!listItems || listItems.length === 0) {
      return (
        <Card variant="subtle" size="lg" className="text-center">
          <p className="text-xl text-[var(--succession-text-primary)] font-medium mb-2">{intl.formatMessage({ id: 'succession.search.list_search.no_results' })}</p>
          <p className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.search.list_search.try_different_criteria' })}</p>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {listItems.map(user => (
          <ListCard 
            key={user.id} 
            user={user}
            currentCompany={listData?.company}
          />
        ))}
      </div>
    );
  };

  const renderMapLayout = () => {
    // Check if profile is complete enough to view search results
    const currentUser = listData?.getCurrentUser;
    const profilePercentage = currentUser?.profile_percentage_complete || 0;
    const isProfileComplete = profilePercentage >= 60;
    const companyFeatures = listData?.company?.features || [];
    const marketplaceApprovalEnabled = companyFeatures.includes('successor_approval_process') ||
                                       companyFeatures.includes('seller_approval_process') ||
                                       companyFeatures.includes('simple_marketplace_approval');
    const approvalState = currentUser?.approval_state;
    // no_motivation ("Here for Research") users have deliberately opted out of the
    // marketplace, so they were never submitted for home-office approval. Don't
    // park them behind the approval wall — let them browse. Visibility of
    // succession users is enforced server-side in the userSearch resolver.
    const optedOutOfMarketplace = currentUser?.motivation === 'no_motivation';
    const isApprovalIneligible = marketplaceApprovalEnabled && !optedOutOfMarketplace && (
      approvalState === 'pending' || approvalState === 0 ||
      approvalState === 'denied' || approvalState === 2
    );

    if (isApprovalIneligible) {
      return <ApprovalIneligibleEmptyState approvalState={approvalState} />;
    }

    if (!isProfileComplete) {
      return <ProfileIncompleteEmptyState profilePercentage={profilePercentage} isSeller={currentUser?.motivation === 'succession' && !!listData?.company?.features?.includes('rj_seller_visibility')} />;
    }

    return (
      <div className="flex gap-4 overflow-hidden" style={{ height: 'calc(100vh - 220px)' }}>
        <div
          className={`transition-all duration-300 flex-shrink-0 min-w-0 ${
            mapExpanded ? 'opacity-0' : 'opacity-100'
          } overflow-hidden`}
          style={{ width: mapExpanded ? 0 : 380 }}
        >
          <Card
            variant="outlined"
            size="lg"
            className="min-h-0 border border-succession-border flex flex-col"
            style={{ height: 'calc(100vh - 220px)' }}
          >
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="p-4 border-b border-succession-border flex-shrink-0 space-y-3">
                {(() => {
                  const currentUser = listData?.getCurrentUser;
                  const profilePercentage = currentUser?.profile_percentage_complete || 0;
                  const isProfileComplete = profilePercentage >= 60;
                  
                  if (!isProfileComplete) {
                    return null;
                  }
                  
                  return (
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-[var(--succession-text-primary)]">
                          {intl.formatMessage({ id: 'succession.search.list_search.partners_found' }, { count: totalCount })}
                        </h3>
                        <p className="text-sm text-[var(--succession-text-secondary)] mt-0.5">
                          {totalCount > 0
                            ? intl.formatMessage({ id: 'succession.search.list_search.showing_range' }, { from: (searchParams.page - 1) * searchParams.perPage + 1, to: Math.min(searchParams.page * searchParams.perPage, totalCount), total: totalCount })
                            : intl.formatMessage({ id: 'succession.search.list_search.click_partner_location' })}
                        </p>
                      </div>
                      {!searchParams.aiMode && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setFiltersOpen(!filtersOpen)}
                          className="rounded-full"
                        >
                          <FunnelIcon className="h-4 w-4 mr-2" />
                          {intl.formatMessage({ id: 'succession.search.list_search.filters_button' })}
                          {activeFilterCount > 0 && (
                            <span className="ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-succession-primary text-white text-xs">
                              {activeFilterCount}
                            </span>
                          )}
                        </Button>
                      )}
                    </div>
                  );
                })()}

                {/* AI Mode Toggle - Using FindBob UI Canon */}
                {(() => {
                  // Hide AI Mode entirely when the ai_search feature is disabled
                  if (!hasAISearchFeature) return null;

                  const currentUser = listData?.getCurrentUser;
                  const profilePercentage = currentUser?.profile_percentage_complete || 0;
                  const isProfileComplete = profilePercentage >= 60;
                  
                  if (!isProfileComplete) {
                    return null;
                  }
                  
                  return (
                    <>
                <Card 
                  variant="outlined" 
                  size="sm"
                  className={`${searchParams.aiMode ? 'border-succession-primary bg-succession-primary-light' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className={`inline-flex items-center justify-center h-8 w-8 rounded-lg transition-theme duration-theme ${
                          searchParams.aiMode 
                            ? 'bg-succession-primary text-white' 
                            : 'bg-succession-bg-tertiary text-succession-text-secondary'
                        }`}
                      >
                        <SparklesIcon className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.search.list_search.ai_mode' })}</span>
                    </div>
                    {searchParams.aiMode ? (
                      <Badge 
                        variant="primary" 
                        size="md"
                        icon={<SparklesIcon className="h-3.5 w-3.5" />}
                      >
                        {intl.formatMessage({ id: 'succession.search.list_search.ai_mode_enabled' })}
                      </Badge>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAIModeChange(true)}
                      >
                        {intl.formatMessage({ id: 'succession.search.list_search.ai_mode_enable_button' })}
                      </Button>
                    )}
                  </div>
                </Card>

                {/* AI Search Section - Using FindBob UI Canon */}
                {searchParams.aiMode && (
                  <Card variant="subtle" size="md" className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-succession-text-primary mb-2 block">
                        {intl.formatMessage({ id: 'succession.search.list_search.describe_looking_for' })}
                      </label>
                      <textarea
                        placeholder={intl.formatMessage({ id: 'succession.search.list_search.ai_search_placeholder' })}
                        value={searchInputValue || ''}
                        onChange={(e) => setSearchInputValue(e.target.value)}
                        className="min-h-[100px] w-full rounded-md border border-succession-border hover:border-succession-border-hover bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted p-3 text-sm transition-theme duration-theme focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring"
                      />
                      <p className="text-xs text-succession-text-secondary mt-2">
                        {intl.formatMessage({ id: 'succession.search.list_search.ai_natural_language_hint' })}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        className="flex-1"
                        onClick={() => handleSearchSubmit()}
                        disabled={listLoading || !searchInputValue}
                      >
                        {listLoading ? intl.formatMessage({ id: 'succession.search.list_search.searching' }) : intl.formatMessage({ id: 'succession.search.list_search.search' })}
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setSearchInputValue('');
                          setConversationHistory([]);
                          handleSearchSubmit({
                            ...searchParams,
                            prompt: '',
                            page: 1
                          });
                        }}
                      >
                        {intl.formatMessage({ id: 'succession.search.list_search.clear' })}
                      </Button>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => handleAIModeChange(false)}
                    >
                      {intl.formatMessage({ id: 'succession.search.list_search.switch_to_manual' })}
                    </Button>
                  </Card>
                )}
                    </>
                  );
                })()}

                {/* Filters Section - rendered independently of AI mode feature flag */}
                {!searchParams.aiMode && (
                  <div className={`${filtersOpen ? 'block' : 'hidden'} space-y-3 pt-3 border-t border-succession-border`}>
                    <div>
                      <label className="text-xs mb-1.5 block text-succession-text-secondary">{intl.formatMessage({ id: 'succession.search.list_search.search_partner_label' })}</label>
                      <input
                        placeholder={intl.formatMessage({ id: 'succession.search.list_search.name_or_practice_id' })}
                        value={searchParams.userName || ''}
                        onChange={(e) => handleSearchChange('userName', e.target.value)}
                        className="h-9 w-full rounded-md border border-succession-border px-3 text-sm"
                      />
                    </div>

                    <div>
                      <label className="text-xs mb-1.5 block text-succession-text-secondary">
                        {intl.formatMessage({
                          id: 'succession.search.list_search.location_label',
                          defaultMessage: regionLabelForCountry(listData?.company?.country),
                        })}
                      </label>
                      <select
                        value={searchParams.province || ''}
                        onChange={(e) => handleSearchChange('province', e.target.value)}
                        className="h-9 w-full rounded-md border border-succession-border px-3 text-sm bg-succession-bg-primary text-succession-text-primary"
                      >
                        {regionOptionsForCountry(listData?.company?.country, intl.locale).map((opt) => (
                          <option key={opt.value || 'any'} value={opt.value}>{opt.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-xs mb-2 block text-succession-text-secondary">{intl.formatMessage({ id: 'succession.search.list_search.interests_label' })}</label>
                      <div className="flex flex-wrap gap-2 min-w-0">
                        {(transitionGoalOptions.length ? transitionGoalOptions : ['Buying', 'Partnering', 'Merging', 'Continuity']).map((goal) => (
                          <Button
                            key={goal}
                            variant={(searchParams.transitionGoalsName || []).includes(goal) ? 'primary' : 'outline'}
                            size="sm"
                            onClick={() => toggleTransitionGoal(goal)}
                            className="h-auto min-h-[32px] px-2 py-1 text-xs text-left break-words max-w-full"
                            style={{ whiteSpace: 'normal' }}
                          >
                            {goal}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs mb-2 block text-succession-text-secondary">
                        {intl.formatMessage({ id: 'succession.search.list_search.min_aum_label' }, { minAum: formatMillionsLabel(minAumMillions) })}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="500"
                        step="50"
                        value={minAumMillions}
                        onChange={(e) => {
                          const nextValue = Number(e.target.value);
                          handleSearchChange(
                            'assetsUnderManagementGt',
                            nextValue > 0 ? nextValue * 1000000 : null
                          );
                        }}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="text-xs mb-2 block text-succession-text-secondary">
                        {intl.formatMessage({ id: 'succession.search.list_search.min_clients_label' }, { minClients })}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="200"
                        step="10"
                        value={minClients}
                        onChange={(e) => {
                          const nextValue = Number(e.target.value);
                          handleSearchChange('numberOfClientsGt', nextValue > 0 ? nextValue : null);
                        }}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="text-xs mb-2 block text-succession-text-secondary">
                        {intl.formatMessage({ id: 'succession.search.list_search.min_years_label' }, { minYears })}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="30"
                        step="5"
                        value={minYears}
                        onChange={(e) => {
                          const nextValue = Number(e.target.value);
                          handleSearchChange('yearsInPracticeGt', nextValue > 0 ? nextValue : null);
                        }}
                        className="w-full"
                      />
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={resetManualFilters}
                        disabled={activeFilterCount === 0}
                      >
                        {intl.formatMessage({ id: 'succession.search.list_search.reset_all' })}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="divide-y divide-succession-border">
              {(() => {
                const currentUser = listData?.getCurrentUser;
                const profilePercentage = currentUser?.profile_percentage_complete || 0;
                const isProfileComplete = profilePercentage >= 60;
                const companyFeatures = listData?.company?.features || [];
                const marketplaceApprovalEnabled = companyFeatures.includes('successor_approval_process') ||
                                       companyFeatures.includes('seller_approval_process') ||
                                       companyFeatures.includes('simple_marketplace_approval');
                const approvalState = currentUser?.approval_state;
                // no_motivation users opted out of the marketplace and were never
                // submitted for approval — let them browse (succession visibility is
                // enforced server-side in the userSearch resolver).
                const optedOutOfMarketplace = currentUser?.motivation === 'no_motivation';
                const isApprovalIneligible = marketplaceApprovalEnabled && !optedOutOfMarketplace && (
                  approvalState === 'pending' || approvalState === 0 ||
                  approvalState === 'denied' || approvalState === 2
                );

                if (isApprovalIneligible) {
                  return (
                    <div className="p-8">
                      <ApprovalIneligibleEmptyState approvalState={approvalState} />
                    </div>
                  );
                }

                if (!isProfileComplete) {
                  return (
                    <div className="p-8">
                      <ProfileIncompleteEmptyState profilePercentage={profilePercentage} isSeller={currentUser?.motivation === 'succession' && !!listData?.company?.features?.includes('rj_seller_visibility')} />
                    </div>
                  );
                }
                
                if (listLoading && !listData) {
                  return (
                    <div className="p-4">
                      {renderLoading()}
                    </div>
                  );
                }
                
                if (listError) {
                  return (
                    <div className="p-4">
                      <Card variant="outlined" size="lg" className="text-center bg-[var(--succession-error-bg)] text-[var(--succession-error)] border-[var(--succession-error)]">
                        <p className="font-medium">{intl.formatMessage({ id: 'succession.search.list_search.error_occurred' })}</p>
                        <p className="text-sm mt-2">{listError.message}</p>
                      </Card>
                    </div>
                  );
                }
                
                if (listItems.length === 0) {
                  return (
                    <div className="p-8 text-center">
                      <p className="text-[var(--succession-text-secondary)] mb-2">{intl.formatMessage({ id: 'succession.search.list_search.no_partners_found' })}</p>
                      <Button variant="outline" size="sm" onClick={resetManualFilters}>
                        {intl.formatMessage({ id: 'succession.search.list_search.reset_filters' })}
                      </Button>
                    </div>
                  );
                }
                
                return listItems.map(user => {
                  const isSelected = selectedUserId === user.id;
                  const isHovered = hoveredUserId === user.id;
                  return (
                    <button
                      key={user.id}
                      className={`w-full p-4 text-left transition-colors ${
                        isSelected
                          ? 'bg-succession-bg-secondary'
                          : isHovered
                            ? 'bg-succession-bg-secondary/50'
                            : 'hover:bg-succession-bg-secondary/50'
                      }`}
                      onClick={() => {
                        setSelectedUserId(user.id);
                        dispatch(openSuccessionDrawer(user.fb_user_name || user.user_name));
                      }}
                      onMouseEnter={() => setHoveredUserId(user.id)}
                      onMouseLeave={() => setHoveredUserId(null)}
                    >
                      <div className="flex items-start gap-3">
                        {(() => {
                          const avatarStyle = getAvatarStyle(user.profile_picture, listData?.company || {});
                          return user.profile_picture ? (
                            <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-succession-border" style={{ backgroundColor: avatarStyle.backgroundColor }}>
                              <img
                                src={avatarStyle.url}
                                alt={user.allowed_name || user.user_name || 'User'}
                                className="w-full h-full object-cover"
                                style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                              />
                            </div>
                          ) : (
                            <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-primary-10 text-succession-primary font-semibold ring-2 ring-succession-border">
                              {getInitials(user.allowed_name || user.user_name)}
                            </div>
                          );
                        })()}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-semibold text-[var(--succession-text-primary)] truncate">
                              {user.allowed_name || user.user_name}
                            </h4>
                            <div className="flex gap-1 flex-shrink-0 items-center">
                              {user.note_count > 0 && (
                                <HeartSolidIcon className="h-4 w-4 text-succession-error fill-succession-error" />
                              )}
                              <ChevronRightIcon className="h-4 w-4 text-succession-text-muted" />
                            </div>
                          </div>
                          <p className="text-sm text-succession-text-secondary flex items-center gap-1 mb-2">
                            <MapPinIcon className="h-3.5 w-3.5" />
                            {user.city || intl.formatMessage({ id: 'succession.search.list_search.location_unavailable' })}{user.province ? `, ${user.province}` : ''}
                          </p>
                          <p className="text-xs text-succession-text-secondary line-clamp-2 mb-2">
                            {user.headline || intl.formatMessage({ id: 'succession.search.list_search.no_headline' })}
                          </p>
                          <div className="flex gap-2 flex-wrap">
                            <span className="text-xs bg-succession-bg-tertiary text-succession-text-secondary px-2 py-1 rounded-full">
                              {intl.formatMessage({ id: 'succession.search.list_search.aum_badge' }, { amount: formatCurrencyShort(user.assets_under_management) })}
                            </span>
                            <span className="text-xs bg-succession-bg-tertiary text-succession-text-secondary px-2 py-1 rounded-full">
                              {intl.formatMessage({ id: 'succession.search.list_search.clients_badge' }, { count: user.number_of_clients || 0 })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
            </div>

            {(() => {
              const currentUser = listData?.getCurrentUser;
              const profilePercentage = currentUser?.profile_percentage_complete || 0;
              const isProfileComplete = profilePercentage >= 60;
              
              if (!isProfileComplete || totalCount <= searchParams.perPage) {
                return null;
              }
              
              return (
              <div className="p-4 border-t border-succession-border flex-shrink-0">
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(searchParams.page - 1)}
                    disabled={searchParams.page <= 1}
                  >
                    {intl.formatMessage({ id: 'succession.search.list_search.previous' })}
                  </Button>
                  <span className="text-sm text-[var(--succession-text-secondary)]">
                    {intl.formatMessage({ id: 'succession.search.list_search.page_of' }, { page: searchParams.page, total: Math.ceil(totalCount / searchParams.perPage) })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(searchParams.page + 1)}
                    disabled={searchParams.page * searchParams.perPage >= totalCount}
                  >
                    {intl.formatMessage({ id: 'succession.search.list_search.next' })}
                  </Button>
                </div>
              </div>
              );
            })()}
          </Card>
        </div>

        <div className="flex-1 min-w-0 relative">
          <Card
            variant="outlined"
            size="lg"
            className="min-h-0 border border-succession-border overflow-hidden"
            style={{ height: 'calc(100vh - 220px)' }}
          >
            <div className="absolute top-4 left-4 z-10 flex gap-2">
              <Button
                variant={mapTypeId === 'roadmap' ? 'secondary' : 'ghost'}
                size="sm"
                className="bg-white/90 backdrop-blur"
                onClick={() => setMapTypeId('roadmap')}
              >
                {intl.formatMessage({ id: 'succession.search.list_search.map_button' })}
              </Button>
              <Button
                variant={mapTypeId === 'satellite' ? 'secondary' : 'ghost'}
                size="sm"
                className="bg-white/90 backdrop-blur"
                onClick={() => setMapTypeId('satellite')}
              >
                {intl.formatMessage({ id: 'succession.search.list_search.satellite_button' })}
              </Button>
            </div>

            <Button
              variant="secondary"
              size="icon"
              className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur"
              onClick={() => setMapExpanded(!mapExpanded)}
            >
              {mapExpanded ? (
                <ArrowsPointingInIcon className="h-4 w-4" />
              ) : (
                <ArrowsPointingOutIcon className="h-4 w-4" />
              )}
            </Button>

            <div className="w-full h-full relative">
              <MapView 
                users={pinsItems}
                apiKey={googleMapsApiKey || listData?.company?.google_maps_api_key}
                mapTypeId={mapTypeId}
                onUserSelect={(user) => setSelectedUserId(user.id)}
                onUserHover={(user) => setHoveredUserId(user ? user.id : null)}
                hoveredUserId={hoveredUserId}
                selectedUserId={selectedUserId}
              />
              {pinsLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                  <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.search.list_search.loading_map_pins' })}</span>
                </div>
              )}
              {pinsError && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <Card variant="outlined" size="sm" className="text-center bg-[var(--succession-error-bg)] text-[var(--succession-error)] border-[var(--succession-error)]">
                    <p className="font-medium">{intl.formatMessage({ id: 'succession.search.list_search.unable_to_load_map' })}</p>
                    <p className="text-xs mt-1">{pinsError.message}</p>
                  </Card>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // Render results based on view mode
  const renderResults = () => {
    if (viewMode === 'map') {
      return renderMapLayout();
    }

    return renderListCards();
  };

  // Gate for rendering the full search chrome (header subtitle, results count, pagination, sidebar, view toggle).
  // Hidden when profile is incomplete OR when user is marketplace-approval ineligible — in both cases the page
  // shows only a centered empty-state card.
  const outerCurrentUser = listData?.getCurrentUser;
  const outerProfilePercentage = outerCurrentUser?.profile_percentage_complete || 0;
  const outerIsProfileComplete = outerProfilePercentage >= 60;
  const outerCompanyFeatures = listData?.company?.features || [];
  const outerMarketplaceApprovalEnabled = outerCompanyFeatures.includes('successor_approval_process') ||
                                          outerCompanyFeatures.includes('seller_approval_process') ||
                                          outerCompanyFeatures.includes('simple_marketplace_approval');
  const outerApprovalState = outerCurrentUser?.approval_state;
  const outerIsApprovalIneligible = outerMarketplaceApprovalEnabled && (
    outerApprovalState === 'pending' || outerApprovalState === 0 ||
    outerApprovalState === 'denied' || outerApprovalState === 2
  );
  const showFullSearchChrome = outerIsProfileComplete && !outerIsApprovalIneligible;

  // View toggle
  const viewToggle = (
    <Card variant="outlined" size="sm" className="flex p-1 w-fit">
      <Button
        variant={viewMode === 'list' ? 'primary' : 'ghost'}
        size="sm"
        onClick={() => handleViewModeChange('list')}
      >
        {intl.formatMessage({ id: 'succession.search.list_search.list_view' })}
      </Button>
      <Button
        variant={viewMode === 'map' ? 'primary' : 'ghost'}
        size="sm"
        onClick={() => handleViewModeChange('map')}
      >
        {intl.formatMessage({ id: 'succession.search.list_search.map_view' })}
      </Button>
    </Card>
  );

  // Sidebar content
  const sidebarContent = (
    <Card variant="elevated" size="lg">
      <SearchSidebar 
        searchParams={searchParams} 
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        onAIModeChange={handleAIModeChange}
        onResetConversation={handleResetConversation}
        conversationHistory={formatConversationHistory(conversationHistory)}
        isLoading={listLoading}
        data={listData}
        countryCode={listData?.company?.country || 'CA'}
        company={listData?.company}
        searchInputValue={searchInputValue}
        setSearchInputValue={setSearchInputValue}
        onKeyPress={handleKeyPress}
      />
    </Card>
  );

  const simpleMarketplaceEnabled = listData?.company?.features?.includes('simple_marketplace_approval');
  const headerTitleKey = simpleMarketplaceEnabled
    ? 'succession.search.list_search.marketplace_listings_title'
    : 'succession.search.list_search.discovery_title';
  const headerDescriptionKey = simpleMarketplaceEnabled
    ? 'succession.search.list_search.marketplace_listings_description'
    : 'succession.search.list_search.discovery_description';

  if (viewMode === 'map') {
    return (
      <>
        <PageContainer style={{ maxWidth: '90rem' }}>
          <div className="flex flex-col lg:flex-row lg:items-start mb-6 gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-semibold text-[var(--succession-text-primary)] whitespace-nowrap">
                {listData?.company?.branded_name ? intl.formatMessage({ id: headerTitleKey }, { brandedName: listData.company.branded_name }) : intl.formatMessage({ id: 'succession.search.list_search.find_succession_partners' })}
              </h1>
              {showFullSearchChrome && (
                <p className="text-sm text-[var(--succession-text-secondary)] mt-1">
                  {intl.formatMessage({ id: headerDescriptionKey }, { agentWord: listData?.company?.agent_word || 'advisor', AgentWord: listData?.company?.agent_word ? listData.company.agent_word.charAt(0).toUpperCase() + listData.company.agent_word.slice(1) : 'Advisor', dealRoomName: getDealRoomBrandedName(listData?.company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' }) })}
                </p>
              )}
            </div>
            {showFullSearchChrome && (
              <div className="flex justify-end shrink-0" style={{ width: '300px' }}>
                {viewToggle}
              </div>
            )}
          </div>
          {renderMapLayout()}
        </PageContainer>
        <SuccessionDrawerWithListener />
      </>
    );
  }

  return (
    <>
      <PageContainer style={{ maxWidth: '70rem' }}>
        {/* Header row */}
        <div className="flex flex-col lg:flex-row lg:items-start mb-6 gap-4">
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-[var(--succession-text-primary)] whitespace-nowrap">
              {listData?.company?.branded_name ? intl.formatMessage({ id: headerTitleKey }, { brandedName: listData.company.branded_name }) : intl.formatMessage({ id: 'succession.search.list_search.find_succession_partners' })}
            </h1>
            {showFullSearchChrome && (
              <p className="text-sm text-[var(--succession-text-secondary)] mt-1">
                {intl.formatMessage({ id: headerDescriptionKey }, { agentWord: listData?.company?.agent_word || 'advisor', AgentWord: listData?.company?.agent_word ? listData.company.agent_word.charAt(0).toUpperCase() + listData.company.agent_word.slice(1) : 'Advisor', dealRoomName: getDealRoomBrandedName(listData?.company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' }) })}
              </p>
            )}
          </div>
          {showFullSearchChrome && (
            <div className="flex justify-end shrink-0" style={{ width: '300px' }}>
              {viewToggle}
            </div>
          )}
        </div>

        {/* Content row - flex layout matching header */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Primary content */}
          <div className={showFullSearchChrome ? "flex-1 space-y-4" : "w-full space-y-4"}>
            <Card variant="elevated" size="lg">
              {/* Results count */}
              {showFullSearchChrome && listData?.userSearch && (
                <Card variant="subtle" size="md" className="mb-4">
                  <span className="font-medium text-[var(--succession-text-primary)]">
                    {intl.formatMessage({ id: 'succession.search.list_search.showing_results' }, {
                      from: (searchParams.page - 1) * searchParams.perPage + 1,
                      to: Math.min(searchParams.page * searchParams.perPage, listData.userSearch.total_count),
                      total: listData.userSearch.total_count
                    })}
                  </span>
                </Card>
              )}
              
              {/* Results */}
              <div className="mb-4">
                {renderResults()}
              </div>
              
              {/* Pagination */}
              {showFullSearchChrome && listData?.userSearch && listData.userSearch.total_count > searchParams.perPage && (
                <Card variant="subtle" size="md" className="flex justify-center items-center">
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => handlePageChange(searchParams.page - 1)}
                    disabled={searchParams.page <= 1}
                    className="rounded-full"
                  >
                    {intl.formatMessage({ id: 'succession.search.list_search.previous' })}
                  </Button>

                  <span className="font-bold mx-4 text-[var(--succession-text-primary)]">
                    {intl.formatMessage({ id: 'succession.search.list_search.page_label' }, { page: searchParams.page })}
                  </span>

                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => handlePageChange(searchParams.page + 1)}
                    disabled={searchParams.page * searchParams.perPage >= listData.userSearch.total_count}
                    className="rounded-full"
                  >
                    {intl.formatMessage({ id: 'succession.search.list_search.next' })}
                  </Button>
                </Card>
              )}
            </Card>
          </div>

          {/* Sidebar - hide when profile incomplete OR user is approval-ineligible */}
          {showFullSearchChrome && (
            <aside className="shrink-0 space-y-4" style={{ width: '300px' }}>
              {sidebarContent}
            </aside>
          )}
        </div>
      </PageContainer>
      <SuccessionDrawerWithListener />
    </>
  );
};

// Main component wrapper with theme provider
const SuccessionListSearch = ({ ...props }) => {
  return (
    <SuccessionThemeProvider company={props.company} user={props.current_user}>
      <SuccessionListSearchContent {...props} />
    </SuccessionThemeProvider>
  );
};

export default SuccessionListSearch; 